import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, TextField, Button, LinearProgress, Alert, CircularProgress, Checkbox, FormControlLabel, Slider, Collapse } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import ListRoundedIcon from "@mui/icons-material/ListRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CourseApi, { Course as CourseT, Section } from "../api/CourseApi";
import BreathingCircle from "../components/BreathingCircle";
import ReportProblemDialog from "../components/ReportProblemDialog";
import { useAuth } from "../context/AuthContext";

function SectionBlock({ section, t, checkedOptions, onToggleOption, text, onText, onTextBlur, scaleValue, onScale, onScaleCommit, chosen, onChoose }: {
  section: Section;
  t: (k: string) => string;
  checkedOptions?: string[];
  onToggleOption?: (o: string) => void;
  text?: string;
  onText?: (v: string) => void;
  onTextBlur?: () => void;
  scaleValue?: number;
  onScale?: (v: number) => void;
  onScaleCommit?: () => void;
  chosen?: string;
  onChoose?: (label: string) => void;
}) {
  const { kind, heading, body, options, scale, media, breathing, choices } = section;
  // "Kies en ontdek": single-select — picking an answer reveals its own
  // supportive reflection. Not graded; switching answers is always allowed.
  if (kind === "choice" && choices?.length && onChoose) {
    const active = choices.find((c) => c.label === chosen);
    return (
      <Box sx={{ my: 2, p: 2.25, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
        <Typography sx={{ fontWeight: 600, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".05em", color: "primary.dark", mb: 0.5 }}>{heading || t("courses.chooseDiscover")}</Typography>
        <Typography sx={{ whiteSpace: "pre-wrap", m: 0 }}>{body}</Typography>
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          {choices.map((c) => {
            const selected = c.label === chosen;
            return (
              <Box key={c.label} onClick={() => onChoose(c.label)} role="button" tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChoose(c.label); } }}
                sx={{
                  p: 1.5, borderRadius: "var(--radius-sm)", cursor: "pointer", userSelect: "none",
                  border: "1px solid", borderColor: selected ? "primary.main" : "var(--border)",
                  bgcolor: selected ? "#fff" : "var(--surface)",
                  boxShadow: selected ? "var(--shadow-sm)" : "none",
                  transition: "border-color .15s ease, box-shadow .15s ease",
                  "&:hover": { borderColor: "primary.main" },
                }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {selected ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} /> : <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 18, color: "var(--border-strong, #CBB9A6)" }} />}
                  <Typography sx={{ fontSize: ".95rem", fontWeight: selected ? 600 : 400 }}>{c.label}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Collapse in={!!active} timeout={300}>
          {active && (
            <Box sx={{ mt: 1.5, p: 1.75, borderRadius: "var(--radius-sm)", bgcolor: "#fff", borderLeft: "3px solid", borderColor: "primary.main" }}>
              <Typography sx={{ whiteSpace: "pre-wrap", m: 0, fontSize: ".95rem" }}>{active.feedback}</Typography>
            </Box>
          )}
        </Collapse>
      </Box>
    );
  }
  if (kind === "media" && media?.url) {
    return (
      <Box sx={{ my: 2 }}>
        {heading && <Typography sx={{ fontWeight: 600, mb: 0.75 }}>{heading}</Typography>}
        {media.mediaType === "audio" ? (
          <audio controls src={media.url} preload="metadata" style={{ width: "100%" }} />
        ) : (
          <video controls src={media.url} poster={media.poster} preload="metadata" style={{ width: "100%", borderRadius: "var(--radius-md)", background: "#000" }} />
        )}
        {body && <Typography sx={{ whiteSpace: "pre-wrap", color: "text.secondary", fontSize: ".9rem", mt: 0.75 }}>{body}</Typography>}
      </Box>
    );
  }
  if (kind === "breathing" && breathing) {
    return (
      <Box sx={{ my: 2 }}>
        {heading && <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{heading}</Typography>}
        {body && <Typography sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}>{body}</Typography>}
        <BreathingCircle inhale={breathing.inhale} hold1={breathing.hold1} exhale={breathing.exhale} hold2={breathing.hold2} cycles={breathing.cycles} />
      </Box>
    );
  }
  if (kind === "tip") {
    return (
      <Box sx={{ my: 2, p: 2, borderRadius: "var(--radius-md)", bgcolor: "#FBF3E2", borderLeft: "3px solid #C2891F" }}>
        <Typography sx={{ fontWeight: 600, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".05em", color: "#8A6410", mb: 0.5 }}>{t("courses.tip")}</Typography>
        <Typography sx={{ whiteSpace: "pre-wrap", m: 0 }}>{body}</Typography>
      </Box>
    );
  }
  if (kind === "exercise") {
    const hasOptions = !!options?.length && !!onToggleOption;
    return (
      <Box sx={{ my: 2, p: 2.25, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
        <Typography sx={{ fontWeight: 600, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".05em", color: "primary.dark", mb: 0.5 }}>{heading || t("courses.exercise")}</Typography>
        <Typography sx={{ whiteSpace: "pre-wrap", m: 0 }}>{body}</Typography>
        {hasOptions && (
          <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 1.5 }}>
            {options!.map((opt) => (
              <FormControlLabel key={opt} sx={{ m: 0, alignItems: "center" }}
                control={<Checkbox size="small" checked={checkedOptions?.includes(opt) || false} onChange={() => onToggleOption!(opt)} sx={{ py: 0.5 }} />}
                label={<Typography sx={{ fontSize: ".92rem" }}>{opt}</Typography>} />
            ))}
          </Box>
        )}
        {hasOptions && onText && (
          <TextField placeholder={t("courses.otherPlaceholder")} value={text || ""} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onText(e.target.value)} onBlur={onTextBlur} multiline minRows={2} fullWidth sx={{ mt: 1.5, bgcolor: "#fff", borderRadius: "var(--radius-sm)" }} />
        )}
        {scale && onScale && (
          <Box sx={{ mt: 3.5, px: 1 }}>
            <Slider
              value={scaleValue ?? Math.round((scale.min + scale.max) / 2)}
              onChange={(_: Event, v: number | number[]) => onScale(v as number)}
              onChangeCommitted={() => onScaleCommit && onScaleCommit()}
              min={scale.min} max={scale.max} step={scale.step || 1}
              valueLabelDisplay="on"
              sx={{ color: "primary.main" }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary", fontSize: ".8rem" }}>
              <span>{scale.minLabel}</span><span>{scale.maxLabel}</span>
            </Box>
          </Box>
        )}
      </Box>
    );
  }
  return (
    <Box sx={{ mb: 1.5 }}>
      {heading && <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{heading}</Typography>}
      <Typography sx={{ whiteSpace: "pre-wrap", m: 0 }}>{body}</Typography>
    </Box>
  );
}

export default function Course() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseT | null>(null);
  // "locked": verdiepend traject zonder toewijzing (API antwoordt 401/403 met
  // code "assignment_required") — we tonen een nette uitleg i.p.v. de inhoud.
  const [locked, setLocked] = useState(false);
  const [completed, setCompleted] = useState<string[]>([]);
  const [journal, setJournal] = useState<Record<string, string>>({});
  const [checks, setChecks] = useState<Record<string, string[]>>({});
  const [scales, setScales] = useState<Record<string, number>>({});
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLocked(false);
    CourseApi.get(slug).then(setCourse).catch((e: any) => {
      const status = e?.response?.status;
      if (status === 401 || status === 403) setLocked(true);
    });
  }, [slug, user]);

  // Flattened lessons (with their module title) for one-at-a-time navigation.
  const flat = useMemo(
    () => (course ? course.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, moduleTitle: m.title }))) : []),
    [course]
  );

  useEffect(() => {
    if (slug && user && flat.length) {
      CourseApi.getProgress(slug).then((p) => {
        setCompleted(p.completed || []);
        setJournal(p.journal || {});
        setChecks(p.checks || {});
        setScales(p.scales || {});
        setChoices(p.choices || {});
        const firstUndone = flat.findIndex((l) => !(p.completed || []).includes(l.key));
        setCurrent(firstUndone === -1 ? 0 : firstUndone);
      }).catch(() => {});
    }
  }, [slug, user, flat.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (locked) {
    return (
      <Box sx={{ bgcolor: "background.default" }}>
        <Container maxWidth="sm" sx={{ py: { xs: 6, sm: 10 } }}>
          <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
            <Typography variant="h1" sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem" }, mb: 1.5 }}>{t("courses.lockedTitle")}</Typography>
            <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("courses.lockedBody")}</Typography>
            {!user && <Alert severity="info" sx={{ mb: 2 }}>{t("courses.lockedLogin")}</Alert>}
            <Button variant="contained" onClick={() => navigate("/zelfhulp")}>{t("courses.lockedCta")}</Button>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!course) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;

  const total = flat.length;
  const lesson = flat[current];
  const allDone = total > 0 && flat.every((l) => completed.includes(l.key));
  const pct = total ? (completed.length / total) * 100 : 0;

  const persist = (comp: string[], jour: Record<string, string>, chks: Record<string, string[]> = checks, scls: Record<string, number> = scales, chcs: Record<string, string> = choices) => { if (user) CourseApi.putProgress(slug!, { completed: comp, journal: jour, checks: chks, scales: scls, choices: chcs }).catch(() => {}); };
  const setJ = (key: string, v: string) => setJournal((j) => ({ ...j, [key]: v }));
  const setScale = (key: string, v: number) => setScales((s) => ({ ...s, [key]: v }));
  const choose = (key: string, label: string) => {
    const nc = { ...choices, [key]: label };
    setChoices(nc); persist(completed, journal, checks, scales, nc);
  };
  const toggleOpt = (key: string, opt: string) => {
    const cur = checks[key] || [];
    const nc = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    const newChecks = { ...checks, [key]: nc };
    setChecks(newChecks); persist(completed, journal, newChecks);
  };
  const markDone = (key: string, done: boolean) => {
    const nc = done ? (completed.includes(key) ? completed : [...completed, key]) : completed.filter((k) => k !== key);
    setCompleted(nc); persist(nc, journal);
  };
  const goTo = (idx: number) => { persist(completed, journal); setCurrent(idx); setTocOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const next = () => {
    if (!completed.includes(lesson.key)) markDone(lesson.key, true);
    if (current < total - 1) goTo(current + 1);
    else { persist(completed.includes(lesson.key) ? completed : [...completed, lesson.key], journal); }
  };

  return (
    <Box sx={{ bgcolor: "background.default", pb: { xs: 6, sm: 8 } }}>
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" }, mb: 0.5 }}>{course.title}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("courses.moduleCount", { count: course.modules.length })} · {t("courses.lessonCount", { count: total })}
        </Typography>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 7, borderRadius: 999, bgcolor: "var(--primary-100)", mt: 1.5, "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" } }} />

        {/* Mobile contents toggle */}
        <Button onClick={() => setTocOpen((o) => !o)} startIcon={<ListRoundedIcon />} sx={{ display: { md: "none" }, mt: 2, textTransform: "none" }}>
          {t("courses.contents")} ({completed.length}/{total})
        </Button>

        <Box sx={{ display: { md: "grid" }, gridTemplateColumns: { md: "270px 1fr" }, gap: { md: 4 }, mt: 2, alignItems: "start" }}>
          {/* Table of contents */}
          <Box sx={{ display: { xs: tocOpen ? "block" : "none", md: "block" }, position: { md: "sticky" }, top: { md: 24 } }}>
            <Box sx={{ p: 2, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
              {course.modules.map((m, mi) => (
                <Box key={mi} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: ".78rem", textTransform: "uppercase", letterSpacing: ".04em", color: "text.secondary", mb: 0.75 }}>{m.title}</Typography>
                  {m.lessons.map((l) => {
                    const idx = flat.findIndex((f) => f.key === l.key);
                    const done = completed.includes(l.key);
                    const active = idx === current;
                    return (
                      <Box key={l.key} onClick={() => goTo(idx)}
                        sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.6, px: 1, borderRadius: "var(--radius-sm)", cursor: "pointer", bgcolor: active ? "var(--primary-50)" : "transparent", "&:hover": { bgcolor: active ? "var(--primary-50)" : "var(--surface-alt)" } }}>
                        {done ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} /> : <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 18, color: "var(--border-strong, #CBB9A6)" }} />}
                        <Typography sx={{ fontSize: ".9rem", fontWeight: active ? 600 : 400, color: active ? "primary.dark" : "text.primary" }}>{l.title}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
            {allDone && user && (
              <Button fullWidth variant="contained" onClick={() => navigate(`/zelfhulp/${slug}/certificaat`)} sx={{ mt: 2, bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }}>
                {t("certificate.cta")}
              </Button>
            )}
          </Box>

          {/* Current lesson */}
          <Box>
            {!user && <Alert severity="info" sx={{ mb: 2 }}>{t("courses.loginToSave")}</Alert>}
            <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
              <Typography variant="caption" sx={{ color: "secondary.dark", textTransform: "uppercase", letterSpacing: ".05em" }}>{lesson.moduleTitle}</Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5, mb: 2, flexWrap: "wrap" }}>
                <Typography variant="h2" sx={{ fontSize: { xs: "1.45rem", sm: "1.7rem" }, m: 0 }}>{lesson.title}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("courses.lessonOf", { current: current + 1, total })}{lesson.durationMin ? ` · ${lesson.durationMin} ${t("courses.min")}` : ""}
                </Typography>
              </Box>

              {(lesson.sections || []).map((s, si) => {
                const inputKey = `${lesson.key}:${si}`;
                return (
                  <SectionBlock key={si} section={s} t={t}
                    checkedOptions={checks[inputKey] || []}
                    onToggleOption={(opt) => toggleOpt(inputKey, opt)}
                    text={journal[inputKey] || ""}
                    onText={(v) => setJ(inputKey, v)}
                    onTextBlur={() => persist(completed, journal)}
                    scaleValue={scales[inputKey]}
                    onScale={(v) => setScale(inputKey, v)}
                    onScaleCommit={() => persist(completed, journal)}
                    chosen={choices[inputKey]}
                    onChoose={(label) => choose(inputKey, label)}
                  />
                );
              })}

              {lesson.journalPrompt && (
                <TextField label={lesson.journalPrompt} value={journal[lesson.key] || ""} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setJ(lesson.key, e.target.value)} onBlur={() => persist(completed, journal)} multiline minRows={3} fullWidth sx={{ mt: 1, mb: 1 }} />
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 3, flexWrap: "wrap" }}>
                <Button variant="text" startIcon={<ArrowBackRoundedIcon />} disabled={current === 0} onClick={() => goTo(current - 1)}>{t("courses.prev")}</Button>
                <Button variant="contained" endIcon={current < total - 1 ? <ArrowForwardRoundedIcon /> : undefined} onClick={next} sx={{ ml: "auto" }}>
                  {current < total - 1 ? t("courses.next") : t("courses.finish")}
                </Button>
              </Box>
            </Box>

            {allDone && (
              <Alert severity="success" sx={{ mt: 3 }} action={user ? <Button color="inherit" size="small" onClick={() => navigate(`/zelfhulp/${slug}/certificaat`)}>{t("certificate.cta")}</Button> : undefined}>
                {t("certificate.completed")}
              </Alert>
            )}

            {/* DSA art. 16: melden van onjuiste/onrechtmatige cursusinhoud */}
            <Box sx={{ mt: 2, textAlign: "right" }}>
              <ReportProblemDialog entityType="course" entityRef={slug} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
